import Highcharts from "highcharts";
import xrangeInit from "highcharts/modules/xrange";
import HighchartsReact from "highcharts-react-official";
import { useMemo } from "react";

import { ParameterTimeSeriesChartResult } from "@/lib/api";
import { DimensionValue } from "@/lib/dimensions";
import { NoDataOverlay } from "@/features/shared/widgets/graphs/no-data-label";
import { useTranslation } from "@/lib/i18n";

import useGraphOptions from "./use-graph-options";
import { resolveGraphChartHeight } from "./resolve-graph-chart-height";
import { useTimeSeriesNoData } from "./hooks/use-time-series-no-data";
import type { ChartTimeBounds } from "./get-shared-chart-time-bounds";
import { PasquillTimeSeriesConfig } from "./graph.types";
import { PASQUILL_CLASS_I18N_KEYS } from "./pasquill-classes";
import { PASQUILL_CLASS_COLORS, pasquillLabelColor } from "./pasquill-colors";
import { buildPasquillTimeline } from "./pasquill-timeline.utils";

xrangeInit(Highcharts);

/** Height per class row, plus room for the title and the x-axis labels. */
const PASQUILL_ROW_HEIGHT_PX = 34;
const PASQUILL_CHART_CHROME_PX = 96;

interface PasquillTimelineProps {
  config: PasquillTimeSeriesConfig;
  data: ParameterTimeSeriesChartResult[];
  currentTime: DimensionValue;
  timeBounds?: ChartTimeBounds;
  height?: number;
}

/**
 * Pasquill stability classes as a block timeline: the series returns 1-6, drawn
 * as one block per run of a class, labelled with its letter.
 *
 * Built as an `xrange` chart rather than a table so it shares the datetime
 * x-axis, plot margins and tick placement of `use-graph-options` with the other
 * time-series charts. Stacked widgets then line their hours up automatically,
 * including after a time-range change, which a CSS grid could only approximate
 * by reimplementing the Highcharts tick algorithm.
 */
const PasquillTimeline = ({
  config,
  data,
  currentTime,
  timeBounds,
  height,
}: PasquillTimelineProps) => {
  const { t } = useTranslation();

  const { rows, stepMs } = useMemo(
    () => buildPasquillTimeline(config, data, timeBounds),
    [config, data, timeBounds],
  );

  const series = useMemo<Highcharts.SeriesOptionsType[]>(() => {
    // A column is centred on its timestamp, so it covers half a step either
    // side. Segments record the datapoint times themselves, so shifting them
    // back half a step puts each block directly under the bar it belongs to.
    const renderOffsetMs = stepMs / 2;

    return [
      {
        type: "xrange",
        name: config.title,
        showInLegend: false,
        // xrange defaults this to 0, which skips the half-step axis padding a
        // column series gets; without it the ticks land on different pixels
        // than the chart above (see Axis.setAxisTranslation, `minPointOffset`).
        pointRange: stepMs,
        // Every block carries its own fill, so the series color is unused.
        borderRadius: 2,
        borderWidth: 0,
        pointPadding: 0,
        groupPadding: 0.1,
        data: rows.flatMap((row, rowIndex) =>
          row.segments.map((segment) => ({
            x: segment.start - renderOffsetMs,
            x2: segment.end - renderOffsetMs,
            y: rowIndex,
            color: PASQUILL_CLASS_COLORS[segment.letter],
            dataLabels: { color: pasquillLabelColor(segment.letter) },
            custom: {
              letter: segment.letter,
              description: t(PASQUILL_CLASS_I18N_KEYS[segment.letter]),
              // Tooltip reports the datapoint times, not the shifted geometry.
              from: segment.start,
              to: segment.end,
            },
          })),
        ),
      },
    ];
  }, [rows, stepMs, config.title, t]);

  const chartHeight = resolveGraphChartHeight(
    height,
    rows.length * PASQUILL_ROW_HEIGHT_PX + PASQUILL_CHART_CHROME_PX,
  );

  const graphOptions = useGraphOptions(
    {
      ...(chartHeight !== undefined ? { chart: { height: chartHeight } } : {}),
      title: {
        text: config.title,
      },
      yAxis: [
        {
          // A category axis bands itself around the rows. Setting min/max by hand
          // instead left the tick interval on the numeric default, which labelled
          // the empty -1 / +1 positions either side of the single row.
          categories: rows.map((row) => row.label),
          // First configured series on top, matching the legend order elsewhere.
          reversed: true,
          title: { text: "" },
          gridLineWidth: 0,
          tickLength: 0,
          // `reserveSpace` is restated because use-graph-options merges `labels` shallowly.
          labels: { reserveSpace: true, style: { fontSize: "0.6875rem" } },
        },
      ],
      xAxis: [
        {
          plotLines: [
            {
              id: "currentTime",
              color: "#01689B", // KNMI blue
              value: +new Date(currentTime.queryStringValue),
              zIndex: 4,
            },
          ],
        },
      ],
      plotOptions: {
        xrange: {
          dataLabels: {
            enabled: true,
            formatter: function (this: Highcharts.PointLabelObject) {
              return (this.point.options.custom?.letter as string) ?? "";
            },
            style: { fontWeight: "bold", textOutline: "none" },
          },
        },
      },
      tooltip: {
        headerFormat: "",
        pointFormatter: function (this: Highcharts.Point) {
          const custom = this.options.custom ?? {};
          const from = Highcharts.dateFormat("%H:%M", custom.from as number);
          const to = Highcharts.dateFormat("%H:%M", custom.to as number);
          return `<strong>${custom.letter}</strong> – ${custom.description}<br/>${from} – ${to}`;
        },
      },
      series,
    },
    timeBounds,
  );

  const noData = useTimeSeriesNoData(config, data);

  return (
    <NoDataOverlay active={noData}>
      <HighchartsReact highcharts={Highcharts} options={graphOptions} />
    </NoDataOverlay>
  );
};

export default PasquillTimeline;
